import { useEffect, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import * as icons from "react-bootstrap-icons";
import { BID_NOTIFICATION_TYPES, outbidPlaceBidTarget } from "../../utils/helper/bidNotifications";
import { dismissBidNotice, extendBidNotice, readBidNotice, subscribeBidNotice } from "./bidNoticeStore";

const ICONS = {
  [BID_NOTIFICATION_TYPES.OUTBID]: icons.ExclamationTriangleFill,
  [BID_NOTIFICATION_TYPES.WON]: icons.TrophyFill,
  [BID_NOTIFICATION_TYPES.LOST]: icons.FlagFill,
};

/**
 * A bid notification's icon (mockup v7): a warning for Outbid, a trophy for Won, a flag for Lost.
 * Coloured by its container's type class (.outbid, .won, .lost).
 */
export const BidNotificationIcon = ({ type, className }) => {
  const Icon = ICONS[type] ?? icons.BellFill;
  return <Icon className={className} aria-hidden="true" />;
};

/**
 * The outbid / won / lost pop-up (#238, mockup v7 States 8-10), drawn by TopNavbar for a Player on
 * every page: fixed at the top right under the top bar, which on Auction Details is to the right of
 * the page title. An icon, the bold title, the server's text and a dismiss (x); Outbid also has
 * Place Bid, which opens the auction's details with the Next Minimum Bid filled in and places
 * nothing (AC14).
 *
 * It hides after BID_NOTICE_MS (AC17), a new one replaces it, and x closes the pop-up only: the
 * notification stays unread on the bell and listed on the Notifications page (AC18). There was no
 * pop-up for notifications before, so the timing is this one's own: held while hovered or
 * focused, then a full BID_NOTICE_MS again.
 */
export const BidNoticePopup = () => {
  const entry = useSyncExternalStore(subscribeBidNotice, readBidNotice);
  const navigate = useNavigate();
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!entry || held) return undefined;
    const id = entry.notice?.id;
    const timer = setTimeout(() => dismissBidNotice(id), Math.max(0, entry.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [entry, held]);

  if (!entry) return null;

  const { notice } = entry;
  const type = notice.notificationType;

  const release = () => {
    setHeld(false);
    extendBidNotice();
  };

  const placeBid = () => {
    const target = outbidPlaceBidTarget(notice);
    dismissBidNotice(notice.id);
    navigate(target.to, { state: target.state });
  };

  return (
    <div
      className={`bid-notice ${String(type).toLowerCase()}`}
      role="status"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={release}
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) release();
      }}
    >
      <BidNotificationIcon type={type} className="bid-notice-icon" />
      <div className="bid-notice-body">
        <b>{notice.title}</b>
        <span>{notice.message}</span>
        {type === BID_NOTIFICATION_TYPES.OUTBID && notice.auctionId ? (
          <button type="button" className="auction-action bid bid-notice-place" onClick={placeBid}>
            Place Bid
          </button>
        ) : null}
      </div>
      <button type="button" className="bid-notice-x" aria-label="Dismiss" onClick={() => dismissBidNotice(notice.id)}>
        <icons.XLg aria-hidden="true" />
      </button>
    </div>
  );
};
